import axios from "axios";
import { api } from "./client";
import type {
  LoginResponse,
  PasswordLoginRequest,
  UserProfileResponse,
  UserSearchResponse,
} from "../types/auth";

/**
 * Map an axios auth error to a user-facing string. Centralised here so
 * Login.tsx doesn't need to import axios just to call `isAxiosError`.
 *
 * Login + registration both use this — pass the appropriate fallback
 * message for the calling flow. Backend-provided `detail` strings are
 * surfaced verbatim so server-side validation errors (e.g. "Employee ID
 * already registered") reach the user without a frontend translation
 * table.
 */
export function friendlyAuthError(err: unknown, fallback: string): string {
  if (axios.isAxiosError(err)) {
    const status = err.response?.status;
    const raw = JSON.stringify(err.response?.data ?? "").toLowerCase();
    if (
      /unauthorized_credentials|invalid_credentials|invalid password/.test(raw)
    )
      return "Invalid email or password.";
    if (/account_disabled|user_disabled|not_active/.test(raw))
      return "Your account is disabled. Contact HR.";
    if (/too_many_requests|rate.?limit/.test(raw))
      return "Too many attempts. Try again in a few minutes.";
    const detail = (err.response?.data as { detail?: string } | undefined)
      ?.detail;
    if (detail && typeof detail === "string") return detail;
    if (status === 401) return "Invalid email or password.";
    if (status === 403) return "You don't have access to sign in.";
    if (status === 429)
      return "Too many attempts. Try again in a few minutes.";
    if (status && status >= 500) return "Server error. Please try again.";
    if (err.code === "ERR_NETWORK")
      return "Network error. Check your connection.";
  }
  return fallback;
}

/**
 * Login-specific wrapper around `friendlyAuthError`. Preserved as a
 * named export so existing call sites (and tests) keep working.
 */
export function friendlyLoginError(err: unknown): string {
  return friendlyAuthError(err, "Sign in failed. Please try again.");
}

export async function passwordLogin(
  body: PasswordLoginRequest,
): Promise<LoginResponse> {
  const { data } = await api.post<LoginResponse>("/auth/password_login", body);
  return data;
}

export async function getUserProfile(
  userId: string,
): Promise<UserProfileResponse> {
  const { data } = await api.get<UserProfileResponse>(
    `/ess/users/profile/${encodeURIComponent(userId)}`,
  );
  return data;
}

export interface UpdateUserProfileRequest {
  units?: string[];
  departments?: string[];
  gender?: string;
}

/** Self-service profile edit (PATCH /users/{id}). The response carries the
 *  near-miss role list, so callers refetch the ESS profile instead of
 *  reading it. */
export async function updateUserProfile(
  userId: string,
  body: UpdateUserProfileRequest,
): Promise<void> {
  await api.patch(`/users/${encodeURIComponent(userId)}`, body);
}

export async function searchUsersByName(
  q: string,
): Promise<UserSearchResponse> {
  const { data } = await api.get<UserSearchResponse>(
    "/users/autocomplete_by_name",
    { params: { q } },
  );
  return data;
}

export interface ListEssUsersByRoleOptions {
  // Optional department filter — matches users whose
  // user_info.departments JSONB array contains this department name.
  department?: string;
}

/** List users that hold the given role (any application bucket),
 *  optionally scoped to a department. ESS-only — backed by
 *  /ess/users/by_role on the backend, which is scoped to the caller's
 *  account. Used by the assign-reviewer card on suggestion detail. */
export async function listEssUsersByRole(
  role: string,
  opts?: ListEssUsersByRoleOptions,
): Promise<UserSearchResponse> {
  const { data } = await api.get<UserSearchResponse>(
    "/ess/users/by_role",
    { params: { role, department: opts?.department } },
  );
  return data;
}

export interface IsAuthorizedRequest {
  action: string;
  entity_type?: string;
  entity_id?: string;
  workflow?: string;
}

export interface IsAuthorizedResponse {
  is_authorized: boolean;
}

export async function isAuthorized(
  body: IsAuthorizedRequest,
): Promise<IsAuthorizedResponse> {
  const { data } = await api.post<IsAuthorizedResponse>(
    "/auth/is_authorized",
    body,
  );
  return data;
}

// ---------------- ESS login + register flow ----------------

export type CheckUserStatus =
  | "needs_registration"
  | "needs_password"
  | "needs_magic_link"
  | "needs_password_or_magic_link";

export interface CheckUserPrefill {
  name?: string | null;
  email?: string | null;
  employee_id?: string | null;
  unit?: string | null;
  departments?: string[] | null;
  gender?: string | null;
}

export interface CheckUserResponse {
  status: CheckUserStatus;
  auth_methods: string[];
  prefill?: CheckUserPrefill | null;
}

export async function checkUser(identifier: string): Promise<CheckUserResponse> {
  const { data } = await api.post<CheckUserResponse>("/ess/auth/check_user", {
    identifier,
  });
  return data;
}

export interface EssRegisterRequest {
  name: string;
  password: string;
  email?: string;
  employee_id?: string;
  account_id?: string;
  unit?: string;
  departments?: string[];
  gender?: string;
  roles?: string[];
}

export interface EssRegisterResponse {
  success: boolean;
  message: string;
  user_id?: string | null;
}

export async function registerEss(
  body: EssRegisterRequest,
): Promise<EssRegisterResponse> {
  const { data } = await api.post<EssRegisterResponse>(
    "/ess/auth/register",
    body,
  );
  return data;
}

// ---------------- Roles for register form multiselect ----------------

export interface RoleDefinition {
  label: string;
  role: string;
}

export interface EssRolesResponse {
  roles: RoleDefinition[];
  // role_groups block from indorama.yaml. Drives any UI that needs to
  // ask "which roles fall under <group>?" — e.g. AdminCustodians or the
  // assign-reviewer card's committee→role derivation.
  groups: Record<string, string[]>;
}

export async function getEssRoles(): Promise<RoleDefinition[]> {
  const { data } = await api.get<EssRolesResponse>("/ess/auth/roles");
  return data.roles;
}

/** Full /ess/auth/roles payload — use when you need both the role
 *  definitions and the role-group membership map. Cheaper than two
 *  round-trips since the backend already serves both in one response. */
export async function getEssRolesFull(): Promise<EssRolesResponse> {
  const { data } = await api.get<EssRolesResponse>("/ess/auth/roles");
  return data;
}

// ---------------- Departments for register form dropdown ----------------

export interface DepartmentOption {
  id: number;
  name: string;
  unit?: string | null;
  is_common?: boolean | null;
}

interface DepartmentsResponse {
  departments: DepartmentOption[];
}

export async function listEssDepartments(): Promise<DepartmentOption[]> {
  // Reuses the existing public /near_miss/departments endpoint — same
  // department list as nmms, since ESS and nmms share the org structure.
  const { data } = await api.get<DepartmentsResponse>("/departments/");
  return data.departments;
}
